"use client"

import { useState, useEffect } from "react"
import { useWallet } from "@solana/wallet-adapter-react"
import { Connection, PublicKey } from "@solana/web3.js"
import { IRMASubscriptionClient } from "@/lib/irma-client-browser"
import { apiClient, type AdminConfig, type AdminConfigBalances } from "@/lib/api-client"
import { CheckCircle2, AlertCircle, Loader2, Info, DollarSign, Settings } from "lucide-react"

interface AdminPanelProps {
  connection: Connection
  irmaMint?: string
}

export function AdminPanel({ connection, irmaMint }: AdminPanelProps) {
  const { publicKey, signTransaction, signAllTransactions } = useWallet()
  const [activeTab, setActiveTab] = useState<"price-oracle" | "admin-config">("price-oracle")
  const [initialPrice, setInitialPrice] = useState("0.025")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [success, setSuccess] = useState("")
  const [priceOracleStatus, setPriceOracleStatus] = useState<{
    initialized: boolean; price?: number; authority?: string
  } | null>(null)

  const [adminConfig, setAdminConfig] = useState<AdminConfig | null>(null)
  const [adminConfigLoading, setAdminConfigLoading] = useState(false)
  const [adminConfigError, setAdminConfigError] = useState("")
  const [adminConfigSuccess, setAdminConfigSuccess] = useState("")
  const [adminConfigBalances, setAdminConfigBalances] = useState<AdminConfigBalances | null>(null)

  const [adminWithdrawalBps, setAdminWithdrawalBps] = useState("6000")
  const [adminWithdrawalTokenAccount, setAdminWithdrawalTokenAccount] = useState("")
  const [updateBps, setUpdateBps] = useState("")
  const [updateAdminAccount, setUpdateAdminAccount] = useState("")
  const [updateMarketAccount, setUpdateMarketAccount] = useState("")

  useEffect(() => {
    if (publicKey) {
      if (!priceOracleStatus) checkPriceOracle()
      if (!adminConfig) checkAdminConfig()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publicKey])

  const checkPriceOracle = async () => {
    if (!publicKey) { setError("Please connect your wallet"); return }
    try {
      setLoading(true); setError("")
      const wallet = { publicKey, signTransaction, signAllTransactions }
      const client = new IRMASubscriptionClient(connection, wallet)
      const priceOraclePDA = client.getPriceOraclePDA()
      try {
        const priceOracle = await client.program.account.priceOracle.fetch(priceOraclePDA)
        const price = Number(priceOracle.price) / 100_000_000
        setPriceOracleStatus({ initialized: true, price, authority: priceOracle.authority.toString() })
        setSuccess(`Price oracle is initialized. Current price: ${price.toFixed(6)}`)
      } catch (err: any) {
        setPriceOracleStatus({ initialized: false })
        setError("Price oracle is not initialized")
      }
    } catch (err: any) {
      setError(err.message || "Failed to check price oracle")
    } finally {
      setLoading(false)
    }
  }

  const initializePriceOracle = async () => {
    if (!publicKey || !signTransaction) { setError("Please connect your wallet"); return }
    const price = parseFloat(initialPrice)
    if (isNaN(price) || price <= 0) { setError("Please enter a valid price (greater than 0)"); return }
    try {
      setLoading(true); setError(""); setSuccess("")
      try {
        const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL || "http://localhost:3008"}/api/price/initialize`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ price }),
        })
        const data = await response.json()
        if (response.ok) {
          setSuccess(`Price oracle initialized successfully! Transaction: ${data.transaction.slice(0, 8)}...`)
          setPriceOracleStatus({ initialized: true, price, authority: publicKey.toString() })
          return
        } else {
          if (data.error?.includes("already initialized") || data.error?.includes("Price oracle already initialized")) {
            setPriceOracleStatus({ initialized: true, price: data.currentPrice, authority: data.authority })
            setSuccess(`Price oracle is already initialized. Current price: ${data.currentPrice?.toFixed(6) || 'N/A'}`)
            setError("")
            return
          }
          throw new Error(data.error || "Backend initialization failed")
        }
      } catch (apiError: any) {
        console.log("Backend initialization failed, trying frontend...", apiError.message)
      }
      const wallet = { publicKey, signTransaction, signAllTransactions }
      const client = new IRMASubscriptionClient(connection, wallet)
      const tx = await client.initializePriceOracle(price, publicKey)
      setSuccess(`Price oracle initialized successfully! Transaction: ${tx.slice(0, 8)}...`)
      setPriceOracleStatus({ initialized: true, price, authority: publicKey.toString() })
    } catch (err: any) {
      setError(err.message || "Failed to initialize price oracle")
      console.error("Initialize error:", err)
    } finally {
      setLoading(false)
    }
  }

  const checkAdminConfig = async () => {
    try {
      setAdminConfigLoading(true); setAdminConfigError("")
      const config = await apiClient.getAdminConfig()
      setAdminConfig(config)
      try {
        const balances = await apiClient.getAdminConfigBalances()
        setAdminConfigBalances(balances)
      } catch (err) {
        console.warn("Could not fetch balances:", err)
      }
    } catch (err: any) {
      if (err.message?.includes("not initialized")) setAdminConfig(null)
      else setAdminConfigError(err.message || "Failed to fetch admin config")
    } finally {
      setAdminConfigLoading(false)
    }
  }

  const initializeAdminConfig = async () => {
    if (!adminWithdrawalTokenAccount) { setAdminConfigError("Admin withdrawal token account is required"); return }
    const bps = parseInt(adminWithdrawalBps)
    if (isNaN(bps) || bps < 0 || bps > 10000) { setAdminConfigError("Admin withdrawal BPS must be between 0 and 10000"); return }
    try {
      setAdminConfigLoading(true); setAdminConfigError(""); setAdminConfigSuccess("")
      const result = await apiClient.initializeAdminConfig(bps, adminWithdrawalTokenAccount, irmaMint)
      setAdminConfigSuccess(`Admin config initialized! Transaction: ${result.transaction.slice(0, 8)}...`)
      await checkAdminConfig()
    } catch (err: any) {
      setAdminConfigError(err.message || "Failed to initialize admin config")
    } finally {
      setAdminConfigLoading(false)
    }
  }

  const updateAdminConfig = async () => {
    const bps = updateBps ? parseInt(updateBps) : undefined
    if (bps !== undefined && (isNaN(bps) || bps < 0 || bps > 10000)) { setAdminConfigError("Admin withdrawal BPS must be between 0 and 10000"); return }
    if (!bps && !updateAdminAccount && !updateMarketAccount) { setAdminConfigError("At least one field must be provided to update"); return }
    try {
      setAdminConfigLoading(true); setAdminConfigError(""); setAdminConfigSuccess("")
      const result = await apiClient.updateAdminConfig(bps, updateAdminAccount || undefined, updateMarketAccount || undefined)
      setAdminConfigSuccess(`Admin config updated! Transaction: ${result.transaction.slice(0, 8)}...`)
      setUpdateBps(""); setUpdateAdminAccount(""); setUpdateMarketAccount("")
      await checkAdminConfig()
    } catch (err: any) {
      setAdminConfigError(err.message || "Failed to update admin config")
    } finally {
      setAdminConfigLoading(false)
    }
  }

  const refreshBalances = async () => {
    try {
      setAdminConfigLoading(true)
      const balances = await apiClient.getAdminConfigBalances()
      setAdminConfigBalances(balances)
    } catch (err: any) {
      setAdminConfigError(err.message || "Failed to fetch balances")
    } finally {
      setAdminConfigLoading(false)
    }
  }

  const inputClass = "w-full bg-[#0A0D14] border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-[#3b4153] outline-none focus:border-[#25e4e8]/50 transition"
  const labelClass = "block text-sm text-[#8B949E] font-medium mb-2"

  return (
    <div className="w-full">
      <div className="bg-[#13161C] rounded-3xl p-3 border border-white/5 shadow-2xl">
        <div className="px-1 mb-3">
          <div className="text-lg font-bold text-white mb-1">Admin Panel</div>
          <div className="text-sm text-[#8B949E] font-medium">Manage price oracle and admin configuration for payment split.</div>
        </div>

        {!publicKey && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-3 mb-3 flex items-center gap-2 text-sm text-red-400">
            <AlertCircle className="h-4 w-4 shrink-0" />
            Please connect your wallet to use the admin panel.
          </div>
        )}

        {/* Tab Switcher */}
        <div className="bg-[#0A0D14] p-1 rounded-xl border border-white/5 inline-flex mb-3">
          <button
            onClick={() => setActiveTab("price-oracle")}
            className={`flex items-center justify-center gap-1.5 py-1.5 px-4 rounded-lg text-xs font-semibold transition ${
              activeTab === "price-oracle" ? "bg-[#25e4e8] text-black" : "text-[#8B949E] hover:text-white"
            }`}
          >
            <DollarSign className="h-3.5 w-3.5" /> Price Oracle
          </button>
          <button
            onClick={() => setActiveTab("admin-config")}
            className={`flex items-center justify-center gap-1.5 py-1.5 px-4 rounded-lg text-xs font-semibold transition ${
              activeTab === "admin-config" ? "bg-[#25e4e8] text-black" : "text-[#8B949E] hover:text-white"
            }`}
          >
            <Settings className="h-3.5 w-3.5" /> Admin Config
          </button>
        </div>

        {/* Price Oracle Tab */}
        {activeTab === "price-oracle" && (
          <div className="space-y-3">
            <button
              onClick={checkPriceOracle}
              disabled={loading || !publicKey}
              className="bg-[#1C1F26] hover:bg-[#252830] border border-white/5 text-white text-sm font-semibold px-4 py-2 rounded-xl transition disabled:opacity-50 flex items-center gap-2"
            >
              {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Checking...</> : "Check Status"}
            </button>

            {priceOracleStatus && (
              <div className={`rounded-xl p-3 border text-sm ${priceOracleStatus.initialized ? "bg-green-500/10 border-green-500/20 text-green-400" : "bg-red-500/10 border-red-500/20 text-red-400"}`}>
                {priceOracleStatus.initialized ? (
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 font-semibold"><CheckCircle2 className="h-4 w-4" /> Price oracle is already initialized</div>
                    <p className="text-[#8B949E]">Current price: ${priceOracleStatus.price?.toFixed(6) || 'N/A'}</p>
                    <p className="text-[#8B949E]">Authority: {priceOracleStatus.authority || 'N/A'}</p>
                  </div>
                ) : (
                  <div className="flex items-center gap-2"><AlertCircle className="h-4 w-4" /> Price oracle is not initialized. Please initialize it below.</div>
                )}
              </div>
            )}

            <div className="bg-[#1C1F26] rounded-2xl p-4 border border-transparent">
              <label className={labelClass}>Initial Price (USD)</label>
              <input
                type="number"
                step="0.000001"
                value={initialPrice}
                onChange={(e) => setInitialPrice(e.target.value)}
                placeholder="0.025"
                disabled={loading || (priceOracleStatus?.initialized === true)}
                className={inputClass}
              />
              <p className="text-xs text-[#8B949E] mt-2">
                {priceOracleStatus?.initialized
                  ? "Price oracle is already initialized. This field is disabled."
                  : "Enter the initial IRMA price in USD (e.g., 0.025 for $0.025 per IRMA)"}
              </p>
            </div>

            <button
              onClick={initializePriceOracle}
              disabled={loading || !publicKey || !initialPrice || (priceOracleStatus?.initialized === true)}
              className="w-full bg-[#25e4e8] hover:bg-[#1dd1d5] disabled:bg-[#8B949E] disabled:cursor-not-allowed text-black font-bold text-[17px] py-4 rounded-xl transition active:scale-[0.98] disabled:active:scale-100 flex items-center justify-center gap-2"
            >
              {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Initializing...</>
                : priceOracleStatus?.initialized ? <><CheckCircle2 className="h-4 w-4" /> Already Initialized</>
                : "Initialize Price Oracle"}
            </button>

            {error && !priceOracleStatus?.initialized && (
              <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-3 flex items-center gap-2 text-sm text-red-400">
                <AlertCircle className="h-4 w-4 shrink-0" />{error}
              </div>
            )}
            {success && (
              <div className="bg-[#25e4e8]/10 border border-[#25e4e8]/20 rounded-xl p-3 flex items-center gap-2 text-sm text-[#25e4e8]">
                <Info className="h-4 w-4 shrink-0" />{success}
              </div>
            )}
          </div>
        )}

        {/* Admin Config Tab */}
        {activeTab === "admin-config" && (
          <div className="space-y-3">
            <div className="px-1">
              <div className="text-sm font-semibold text-white mb-1">Admin Config - Payment Split</div>
              <div className="text-xs text-[#8B949E]">Configure how subscription payments are split between admin withdrawal account and market pool.</div>
            </div>

            <div className="flex gap-2">
              <button
                onClick={checkAdminConfig}
                disabled={adminConfigLoading || !publicKey}
                className="bg-[#1C1F26] hover:bg-[#252830] border border-white/5 text-white text-sm font-semibold px-4 py-2 rounded-xl transition disabled:opacity-50 flex items-center gap-2"
              >
                {adminConfigLoading ? <><Loader2 className="h-4 w-4 animate-spin" /> Checking...</> : "Check Status"}
              </button>
              {adminConfig && (
                <button
                  onClick={refreshBalances}
                  disabled={adminConfigLoading}
                  className="bg-[#1C1F26] hover:bg-[#252830] border border-white/5 text-white text-sm font-semibold px-4 py-2 rounded-xl transition disabled:opacity-50"
                >
                  Refresh Balances
                </button>
              )}
            </div>

            {adminConfig ? (
              <div className="space-y-3">
                <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-3 text-sm text-green-400 space-y-1">
                  <div className="flex items-center gap-2 font-semibold"><CheckCircle2 className="h-4 w-4" /> Admin config is initialized</div>
                  <p className="text-[#8B949E]">Admin Withdrawal: {adminConfig.adminWithdrawalBps / 100}%</p>
                  <p className="text-[#8B949E]">Market Pool: {(10000 - adminConfig.adminWithdrawalBps) / 100}%</p>
                  <p className="text-[#8B949E]">Admin Account: {adminConfig.adminWithdrawalTokenAccount.slice(0, 8)}...</p>
                  <p className="text-[#8B949E]">Market Pool Account: {adminConfig.marketPoolTokenAccount.slice(0, 8)}...</p>
                </div>

                {adminConfigBalances && (
                  <div className="bg-[#1C1F26] rounded-2xl p-4 border border-transparent">
                    <div className="text-sm font-semibold text-white mb-3">Token Account Balances</div>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between"><span className="text-[#8B949E]">Admin Withdrawal:</span><span className="font-medium text-white">{adminConfigBalances.balances.adminWithdrawal.balanceFormatted}</span></div>
                      <div className="flex justify-between"><span className="text-[#8B949E]">Market Pool:</span><span className="font-medium text-white">{adminConfigBalances.balances.marketPool.balanceFormatted}</span></div>
                      <div className="flex justify-between pt-2 border-t border-white/5"><span className="font-semibold text-white">Total:</span><span className="font-semibold text-[#25e4e8]">{adminConfigBalances.balances.total.balanceFormatted}</span></div>
                    </div>
                  </div>
                )}

                <div className="bg-[#1C1F26] rounded-2xl p-4 border border-transparent space-y-3">
                  <div className="text-sm font-semibold text-white">Update Admin Config</div>
                  <div>
                    <label className={labelClass}>Admin Withdrawal BPS (0-10000, e.g., 6000 = 60%)</label>
                    <input type="number" value={updateBps} onChange={(e) => setUpdateBps(e.target.value)} placeholder={adminConfig.adminWithdrawalBps.toString()} className={inputClass} />
                  </div>
                  <div>
                    <label className={labelClass}>Admin Withdrawal Token Account (optional)</label>
                    <input value={updateAdminAccount} onChange={(e) => setUpdateAdminAccount(e.target.value)} placeholder={adminConfig.adminWithdrawalTokenAccount} className={inputClass} />
                  </div>
                  <div>
                    <label className={labelClass}>Market Pool Token Account (optional)</label>
                    <input value={updateMarketAccount} onChange={(e) => setUpdateMarketAccount(e.target.value)} placeholder={adminConfig.marketPoolTokenAccount} className={inputClass} />
                  </div>
                  <button
                    onClick={updateAdminConfig}
                    disabled={adminConfigLoading || (!updateBps && !updateAdminAccount && !updateMarketAccount)}
                    className="w-full bg-[#25e4e8] hover:bg-[#1dd1d5] disabled:bg-[#8B949E] disabled:cursor-not-allowed text-black font-bold text-[17px] py-4 rounded-xl transition active:scale-[0.98] disabled:active:scale-100 flex items-center justify-center gap-2"
                  >
                    {adminConfigLoading ? <><Loader2 className="h-4 w-4 animate-spin" /> Updating...</> : "Update Admin Config"}
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-3 flex items-center gap-2 text-sm text-red-400">
                  <AlertCircle className="h-4 w-4 shrink-0" /> Admin config is not initialized. Please initialize it below.
                </div>
                <div className="bg-[#1C1F26] rounded-2xl p-4 border border-transparent space-y-3">
                  <div>
                    <label className={labelClass}>Admin Withdrawal BPS (0-10000)</label>
                    <input type="number" value={adminWithdrawalBps} onChange={(e) => setAdminWithdrawalBps(e.target.value)} placeholder="6000" className={inputClass} />
                    <p className="text-xs text-[#8B949E] mt-1">Basis points (6000 = 60% to admin, 40% to market pool)</p>
                  </div>
                  <div>
                    <label className={labelClass}>Admin Withdrawal Token Account *</label>
                    <input value={adminWithdrawalTokenAccount} onChange={(e) => setAdminWithdrawalTokenAccount(e.target.value)} placeholder="Enter admin's token account public key" className={inputClass} />
                    <p className="text-xs text-[#8B949E] mt-1">Admin's token account for receiving payment split (for future MoonPay conversion)</p>
                  </div>
                  <button
                    onClick={initializeAdminConfig}
                    disabled={adminConfigLoading || !adminWithdrawalTokenAccount}
                    className="w-full bg-[#25e4e8] hover:bg-[#1dd1d5] disabled:bg-[#8B949E] disabled:cursor-not-allowed text-black font-bold text-[17px] py-4 rounded-xl transition active:scale-[0.98] disabled:active:scale-100 flex items-center justify-center gap-2"
                  >
                    {adminConfigLoading ? <><Loader2 className="h-4 w-4 animate-spin" /> Initializing...</> : "Initialize Admin Config"}
                  </button>
                </div>
              </div>
            )}

            {adminConfigError && (
              <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-3 flex items-center gap-2 text-sm text-red-400">
                <AlertCircle className="h-4 w-4 shrink-0" />{adminConfigError}
              </div>
            )}
            {adminConfigSuccess && (
              <div className="bg-[#25e4e8]/10 border border-[#25e4e8]/20 rounded-xl p-3 flex items-center gap-2 text-sm text-[#25e4e8]">
                <CheckCircle2 className="h-4 w-4 shrink-0" />{adminConfigSuccess}
              </div>
            )}

            {adminConfig && (
              <div className="space-y-3 pt-3 border-t border-white/5">
                <div className="text-sm font-semibold text-white">MoonPay Conversion History</div>
                <MoonPayConversionsList />
              </div>
            )}
          </div>
        )}

        {/* Instructions */}
        <div className="mt-4 pt-4 border-t border-white/5 space-y-2">
          <p className="text-sm font-semibold text-white">Instructions:</p>
          <ol className="text-sm text-[#8B949E] space-y-1 list-decimal list-inside">
            <li>Connect your wallet (must be admin/authority)</li>
            <li>Use "Price Oracle" tab to initialize price oracle</li>
            <li>Use "Admin Config" tab to configure payment split</li>
            <li>Admin config controls how subscription payments are split between admin and market pool</li>
          </ol>
        </div>
      </div>
    </div>
  )
}

function MoonPayConversionsList() {
  const [conversions, setConversions] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")

  useEffect(() => {
    loadConversions()
    const interval = setInterval(loadConversions, 30000)
    return () => clearInterval(interval)
  }, [])

  const loadConversions = async () => {
    try {
      setLoading(true); setError("")
      const data = await apiClient.getMoonPayConversions(20)
      setConversions(data.conversions || [])
    } catch (err: any) {
      setError(err.message || "Failed to load conversions")
    } finally {
      setLoading(false)
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'text-green-400'
      case 'failed': case 'cancelled': return 'text-red-400'
      case 'processing': return 'text-yellow-400'
      default: return 'text-[#8B949E]'
    }
  }

  if (loading && conversions.length === 0) {
    return (
      <div className="bg-[#1C1F26] rounded-2xl p-4 flex items-center justify-center gap-2">
        <Loader2 className="h-4 w-4 animate-spin text-[#25e4e8]" />
        <span className="text-sm text-[#8B949E]">Loading conversions...</span>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-3 flex items-center gap-2 text-sm text-red-400">
        <AlertCircle className="h-4 w-4 shrink-0" />{error}
      </div>
    )
  }

  if (conversions.length === 0) {
    return (
      <div className="bg-[#1C1F26] rounded-2xl p-4 flex items-center gap-2 text-sm text-[#8B949E]">
        <Info className="h-4 w-4 shrink-0" />
        No conversions yet. Conversions will appear here when tokens are automatically converted to fiat.
      </div>
    )
  }

  return (
    <div className="space-y-2">
      <div className="flex justify-between items-center">
        <p className="text-sm font-medium text-white">Recent Conversions</p>
        <button
          onClick={loadConversions}
          disabled={loading}
          className="bg-[#1C1F26] hover:bg-[#252830] border border-white/5 text-white text-xs font-semibold px-3 py-1.5 rounded-lg transition disabled:opacity-50"
        >
          {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : "Refresh"}
        </button>
      </div>
      <div className="space-y-2 max-h-96 overflow-y-auto">
        {conversions.map((conversion: any) => (
          <div key={conversion._id} className="bg-[#1C1F26] rounded-xl p-3 text-sm">
            <div className="flex justify-between items-start">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-white">{conversion.irmaAmountFormatted}</span>
                  <span className="text-[#8B949E]">→</span>
                  {conversion.fiatAmount && (
                    <span className="font-medium text-white">
                      ${parseFloat(conversion.fiatAmount).toFixed(2)} {conversion.fiatCurrency || 'USD'}
                    </span>
                  )}
                </div>
                <p className="text-xs text-[#8B949E]">{new Date(conversion.createdAt).toLocaleString()}</p>
                {conversion.moonpayTransactionId && (
                  <p className="text-xs text-[#8B949E]">MoonPay ID: {conversion.moonpayTransactionId.slice(0, 8)}...</p>
                )}
              </div>
              <span className={`text-xs font-medium ${getStatusColor(conversion.status)}`}>
                {conversion.status.toUpperCase()}
              </span>
            </div>
            {conversion.errorMessage && (
              <p className="text-xs text-red-400 mt-1">{conversion.errorMessage}</p>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
